import { Icon } from '@iconify/react';
import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useRegisterOverlay } from '@/contexts/OverlayStackContext';
import { impactLight } from '@/utils/nativeHaptics';
import { MenuItemConfig } from './menuConfig';

interface MobileBottomNavProps {
  items: MenuItemConfig[];
}

const MobileBottomNav: React.FC<MobileBottomNavProps> = ({ items }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [showMore, setShowMore] = useState(false);

  const visibleNavItems = items.filter((item) => !item.hideOnMobile);

  // Maximum items to show directly (including "More" if needed)
  const MAX_VISIBLE_ITEMS = 4;

  // Logic:
  // If items <= 4, show all 4.
  // If items > 4, show 3 + "More"
  const showMoreButton = visibleNavItems.length > MAX_VISIBLE_ITEMS;
  const visibleItems = showMoreButton ? visibleNavItems.slice(0, 3) : visibleNavItems;
  const moreItems = showMoreButton ? visibleNavItems.slice(3) : [];

  const isActive = (path: string) => {
    if (path === '/admin' || path === '/' || path === '/client' || path === '/host') {
      return location.pathname === path || location.pathname === `${path}/`;
    }
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  const handleItemClick = (path: string) => {
    impactLight();
    if (isActive(path)) {
      document.querySelectorAll('main').forEach((container) => {
        container.scrollTo({ top: 0 });
      });
      setShowMore(false);
      return;
    }
    navigate(path, { replace: true });
    setShowMore(false);
  };

  useRegisterOverlay(showMore, () => setShowMore(false));

  if (
    location.pathname.startsWith('/host/menu') ||
    location.pathname.startsWith('/host/profile-settings/edit-info')
  ) {
    return null;
  }

  return (
    <>
      {/* Slide-up Menu for "More" */}
      {showMore && (
        <div
          className="fixed inset-0 z-50 flex flex-col justify-end bg-black bg-opacity-50"
          onClick={() => setShowMore(false)}
        >
          <div
            className="bg-white rounded-t-2xl px-4 pt-4 pb-safe-4 max-h-[70vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-center mb-4">
              <div className="w-12 h-1.5 bg-gray-300 rounded-full"></div>
            </div>
            <div className="grid grid-cols-4 gap-4">
              {moreItems.map((item) => (
                <button
                  key={item.path}
                  onClick={() => handleItemClick(item.path)}
                  className={`flex flex-col items-center justify-center p-2 text-xs rounded-lg active:scale-95 active:opacity-80 ${
                    isActive(item.path) ? 'bg-primary-50 text-primary-500' : 'text-gray-600'
                  }`}
                >
                  <div className="w-10 h-10 mb-2 bg-gray-100 rounded-full flex items-center justify-center">
                    <Icon icon={item.icon} className="w-6 h-6" />
                  </div>
                  <span className="text-center leading-tight truncate w-full">{item.label}</span>
                </button>
              ))}
            </div>
            <button
              onClick={() => setShowMore(false)}
              className="w-full mt-6 py-3 text-center text-gray-500 font-medium active:scale-[0.98] active:opacity-80"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Bottom Nav Bar */}
      {location.pathname.startsWith('/host') && visibleItems.length === 4 ? (
        <nav className="fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-gray-100 dark:border-gray-800 shadow-[0_-1px_0_rgba(0,0,0,0.05)] px-1.5 pt-1.5 pb-nav-safe md:hidden grid grid-cols-4 gap-1 select-none">
          {visibleItems.map((item) => {
            const active = isActive(item.path);

            return (
              <button
                key={item.path}
                onClick={() => handleItemClick(item.path)}
                type="button"
                className={`flex flex-col items-center justify-center gap-[3px] py-2 px-1 rounded-xl transition-all duration-150 cursor-pointer active:scale-95 active:opacity-80 ${
                  active
                    ? 'bg-[#FFF4EE] text-[#FC480D]'
                    : 'bg-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                <Icon
                  icon={item.icon}
                  className={`w-6 h-6 transition-transform duration-150 ${
                    active ? 'scale-105 text-[#FC480D]' : 'text-gray-600'
                  }`}
                />
                <span
                  className={`text-[10.5px] leading-tight transition-colors ${
                    active ? 'font-semibold text-[#FC480D]' : 'font-medium text-gray-600'
                  }`}
                >
                  {item.label}
                </span>
              </button>
            );
          })}
        </nav>
      ) : (
        <nav className="fixed bottom-0 left-0 right-0 z-40 bg-white border-t pb-safe">
          <div className="h-16 flex">
            {visibleItems.map((item) => {
              const active = isActive(item.path);

              return (
                <button
                  key={item.path}
                  onClick={() => handleItemClick(item.path)}
                  className={`flex-1 h-full flex flex-col items-center justify-center text-xs active:scale-95 active:opacity-80 ${
                    active ? 'text-primary-500' : 'text-gray-600'
                  }`}
                >
                  <div className="w-6 h-6 mb-1">
                    <Icon icon={item.icon} className="w-5 h-5" />
                  </div>
                  <span className="truncate max-w-[64px]">{item.label}</span>
                </button>
              );
            })}

            {showMoreButton && (
              <button
                onClick={() => {
                  impactLight();
                  setShowMore(true);
                }}
                className={`flex-1 h-full flex flex-col items-center justify-center text-xs active:scale-95 active:opacity-80 ${
                  showMore ? 'text-primary-500' : 'text-gray-600'
                }`}
              >
                <div className="w-6 h-6 mb-1">
                  <Icon icon="mdi:dots-horizontal" className="w-5 h-5" />
                </div>
                <span>More</span>
              </button>
            )}
          </div>
        </nav>
      )}
    </>
  );
};

export default MobileBottomNav;
